<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="92%"
    top="9vh"
    custom-class="column-settings-dialog"
    :close-on-click-modal="false"
    append-to-body
  >
    <div slot="title" class="dialog-heading">
      <h3>列表字段设置</h3>
      <p>{{ reportTitle }} · {{ pageLabel }}</p>
    </div>

    <div class="settings-toolbar">
      <div class="selection-status">
        <el-checkbox
          :value="allSelected"
          :indeterminate="isIndeterminate"
          @change="handleSelectAll"
          >全选</el-checkbox
        >
        <span>已选择 {{ selectedCount }}/{{ draftColumns.length }} 个字段</span>
      </div>
      <el-input
        v-model.trim="keyword"
        class="field-search"
        prefix-icon="el-icon-search"
        placeholder="搜索字段"
        clearable
      />
    </div>
    <p class="drag-tip">勾选控制列表展示；按住左侧拖动图标可调整列顺序</p>

    <draggable
      v-model="draftColumns"
      class="field-grid"
      :style="fieldGridStyle"
      handle=".drag-handle"
      :animation="180"
      ghost-class="field-item-ghost"
    >
      <div
        v-for="column in draftColumns"
        v-show="matchesKeyword(column)"
        :key="column.prop"
        class="field-item"
      >
        <i class="el-icon-s-operation drag-handle" />
        <el-checkbox v-model="column.visible">{{ column.label }}</el-checkbox>
      </div>
    </draggable>

    <div slot="footer" class="settings-footer">
      <span class="footer-tip">拖动排序后，列表将按新顺序展示</span>
      <div>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "ColumnSettingsDialog",
  components: { draggable },
  props: {
    reportTitle: { type: String, default: "用户合约交易与资产监控表" },
    visible: { type: Boolean, default: false },
    columns: { type: Array, default: () => [] },
    pageLabel: { type: String, default: "汇总页" },
  },
  data() {
    return {
      keyword: "",
      draftColumns: [],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    selectedCount() {
      return this.draftColumns.filter((column) => column.visible).length;
    },
    allSelected() {
      return (
        this.draftColumns.length > 0 &&
        this.selectedCount === this.draftColumns.length
      );
    },
    isIndeterminate() {
      return this.selectedCount > 0 && !this.allSelected;
    },
    fieldGridStyle() {
      return {
        gridTemplateRows: `repeat(${Math.ceil(
          this.draftColumns.length / 3,
        )}, 40px)`,
      };
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.keyword = "";
        this.draftColumns = this.columns.map((column) => ({ ...column }));
      }
    },
  },
  methods: {
    matchesKeyword(column) {
      return (
        !this.keyword ||
        column.label.toLowerCase().includes(this.keyword.toLowerCase())
      );
    },
    handleSelectAll(checked) {
      this.draftColumns.forEach((column) => {
        column.visible = checked;
      });
    },
    handleSave() {
      if (!this.selectedCount) {
        this.$message.warning("请至少保留一个展示字段");
        return;
      }
      this.$emit(
        "save",
        this.draftColumns.map((column) => ({ ...column })),
      );
      this.dialogVisible = false;
    },
  },
};
</script>

<style lang="less" scoped>
.dialog-heading {
  h3 {
    margin: 0;
    color: #1f2a3d;
    font-size: 24px;
    line-height: 34px;
  }

  p {
    margin: 8px 0 0;
    color: #738096;
    font-size: 13px;
  }
}

.settings-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.selection-status {
  display: flex;
  align-items: center;
  gap: 34px;
  color: #5e6a7d;
}

.field-search {
  width: 268px;
}

.drag-tip {
  margin: 0 0 20px;
  color: #98a3b5;
  font-size: 13px;
}

.field-grid {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  gap: 8px 14px;
  min-height: 328px;
}

.field-item {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0 16px;
  background: #fafbfd;
  border: 1px solid #dfe5ee;
  border-radius: 7px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #9db8ff;
    box-shadow: 0 2px 8px rgba(47, 95, 237, 0.08);
  }
}

.drag-handle {
  margin-right: 10px;
  color: #97a4b8;
  cursor: move;
}

.field-item-ghost {
  opacity: 0.45;
  border-color: #2f66f5;
}

.settings-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.footer-tip {
  color: #98a3b5;
  font-size: 13px;
}

::v-deep .column-settings-dialog {
  max-width: 1372px;
  margin-bottom: 0;
  border-radius: 16px;
  box-shadow: 10px 14px 0 rgba(17, 30, 52, 0.92);

  .el-dialog__header {
    padding: 23px 34px 14px;
    border-bottom: 1px solid #e4e8ef;
  }

  .el-dialog__headerbtn {
    top: 27px;
    right: 28px;
    font-size: 26px;
  }

  .el-dialog__body {
    padding: 16px 34px 22px;
  }

  .el-dialog__footer {
    padding: 18px 34px;
    border-top: 1px solid #e4e8ef;
  }

  .el-input__inner {
    height: 43px;
    border-radius: 8px;
  }

  .el-checkbox__label {
    color: #344054;
  }

  .el-button {
    min-width: 93px;
  }
}

@media (max-width: 1100px) {
  .field-grid {
    grid-auto-flow: row;
    grid-template-rows: none;
    grid-template-columns: repeat(2, minmax(240px, 1fr));
    max-height: 52vh;
    overflow-y: auto;
  }
}
</style>
